/**
 * Guide copy for the transcript, creator-data, and calculator tools. The shape and
 * the reason this file exists are documented on `toolGuide.ts`.
 */

import type { ToolGuide } from './toolGuideShape';

export const TOOL_GUIDE_CREATOR: Readonly<Record<string, ToolGuide>> = {
  'social-transcript': {
    fields: [
      'The spoken words as plain text',
      'Which of the five networks the link resolved to',
      'The time the transcript was retrieved',
    ],
    steps: [
      'Paste the link to a public TikTok, YouTube, Instagram, Facebook, or X video.',
      'Clear the short human check and start the run.',
      'Read the transcript, then copy it or send it into your own workflow.',
    ],
    limits: [
      ['Accepts', 'One public video link per run, from any of the five supported networks.'],
      ['Returns', 'The spoken words, plus the network the link resolved to.'],
      ['Stops at', 'Silent video and anything behind a private account.'],
    ],
  },
  'youtube-channel-transcripts': {
    fields: [
      'Each recent video the channel lookup returned',
      'The transcript available for it',
      'How many rows were retrieved',
    ],
    steps: [
      'Enter a channel handle such as @GoogleDevelopers, or paste the channel URL.',
      'Run it and let the latest videos resolve one at a time.',
      'Work through the collected transcripts in one place instead of tab by tab.',
    ],
    limits: [
      ['Accepts', 'A public channel handle or channel URL.'],
      ['Returns', 'The recent videos the channel lookup gives back, each with its transcript.'],
      ['Stops at', 'Full channel history, which is the batch path rather than this page.'],
    ],
  },
  'profile-viewer': {
    fields: [
      'Handle and display name',
      'Bio',
      'Follower and post counts',
      'A link to the live public profile',
    ],
    steps: [
      'Choose the network, then type the handle you want to read.',
      'Run the lookup and let the profile resolve.',
      'Compare the returned fields against another network without changing shape.',
    ],
    limits: [
      ['Accepts', 'One public handle on one supported network per run.'],
      ['Returns', 'Identity, counts, and the link back to the live profile.'],
      ['Stops at', 'Private accounts and anything a viewer would need to be logged in to see.'],
    ],
  },
  'snapchat-story-viewer': {
    fields: [
      'Display name, handle, and bio',
      'Each current story in order, with its title and position',
      'A public preview image per story where one came back',
      'The time the profile was retrieved',
    ],
    steps: [
      'Type a Snapchat username such as fcbarcelona, or paste the profile URL.',
      'Run it to pull the public profile and whatever story is live right now.',
      'Open the source link when you want to confirm a field yourself.',
    ],
    limits: [
      ['Accepts', 'A public Snapchat username or profile URL.'],
      ['Returns', 'Public profile fields and current story metadata.'],
      ['Stops at', 'Expired stories, which Snapchat no longer exposes to anyone.'],
    ],
  },
  'tiktok-audience-analyzer': {
    fields: [
      'Each country with its share of the audience',
      'The raw count behind every share',
      'The leading market, called out first',
      'The time the distribution was taken',
    ],
    steps: [
      'Enter the TikTok handle you want to understand.',
      'Run it to pull the public country distribution for that account.',
      'Read the leading market first, then open the full country list underneath.',
    ],
    limits: [
      ['Accepts', 'One public TikTok handle per run.'],
      ['Returns', 'Country share, with the retrieval time printed beside it.'],
      ['Stops at', 'Age and gender splits, which stay inside the owner-only dashboard.'],
    ],
  },
  'username-checker': {
    fields: [
      'One row per supported network',
      'Whether a public profile exists there',
      'A link to the profile on every taken row',
    ],
    steps: [
      'Type the username you are thinking of taking.',
      'Run one sweep across every supported network.',
      'Follow the link on each taken result to see who holds it.',
    ],
    limits: [
      ['Accepts', 'One username string, checked everywhere at once.'],
      ['Returns', 'Taken or not found per network, with a link where a profile exists.'],
      ['Stops at', 'Registration itself, which still happens on each network.'],
    ],
  },
  'instagram-non-followers': {
    steps: [
      'Request your data from Instagram and wait for the export to arrive.',
      'Load followers_1.json and following.json out of that download.',
      'Read the difference, computed on your own machine.',
    ],
    limits: [
      ['Accepts', 'The two JSON files from an official Instagram data export.'],
      ['Returns', 'Accounts you follow that do not follow you back.'],
      ['Stops at', 'The network boundary: nothing is uploaded and nothing is sent to Instagram.'],
    ],
  },
  'engagement-rate-calculator': {
    fields: [
      'The visible sample you entered',
      'Average engagement rate',
      'Median engagement rate',
      'The reach-based rate when you supply reach',
    ],
    steps: [
      'Pick the platform so the input labels match what you are counting.',
      'Enter followers, then the interactions from the posts you are measuring.',
      'Add reach when you have it, and read both rates side by side.',
    ],
    limits: [
      ['Accepts', 'Numbers you supply, from any account you can see the figures for.'],
      ['Returns', 'The rate, with every input left on screen to check.'],
      ['Stops at', 'Grading: there is no benchmark, because a fair one moves with audience size.'],
    ],
  },
  'creator-earnings-calculator': {
    steps: [
      'Enter audience size, typical views, and the engagement you actually see.',
      'Adjust the rate assumptions to your niche and market.',
      'Take the range into a negotiation as a starting position.'],
    limits: [
      ['Accepts', 'Your own audience figures and whatever rate you believe applies.'],
      ['Returns', 'A low-to-high sponsored-post range built from those inputs.'],
      ['Stops at', 'Anything a brand has agreed to, which no calculator can know.'],
    ],
  },
  'youtube-shorts-earnings': {
    fields: [
      'Recent public Shorts and their view counts',
      'Observed posting cadence',
      'A low and high monthly revenue figure',
    ],
    steps: [
      'Enter a channel handle such as @zachking, or paste the channel URL.',
      'Run it to read the recent public Shorts and their view counts.',
      'Set the low and high RPM assumptions to bracket the monthly estimate.'],
    limits: [
      ['Accepts', 'Any public channel, including one you do not own.'],
      ['Returns', 'A monthly range, with the posting cadence that produced it.'],
      ['Stops at', 'Real payouts, which depend on RPM figures only the channel owner sees.'],
    ],
  },
  'twitch-earnings-calculator': {
    steps: [
      'Enter average viewers, stream hours, subscriptions, and bits from your dashboard.',
      'Open the payout and CPM assumptions and match them to your contract.',
      'Read the monthly total, split by where the income comes from.'],
    limits: [
      ['Accepts', 'Figures you read off your own Twitch dashboard.'],
      ['Returns', 'Estimated monthly platform income by source.'],
      ['Stops at', 'Sponsorships and affiliate income, which Twitch never reports.'],
    ],
  },
  'x-non-followers': {
    steps: [
      'Type the X handle you want to audit.',
      'Clear the human check and let the follower and following samples load.',
      'Reveal the accounts that do not follow back, and export them if you want them.'],
    limits: [
      ['Accepts', 'Any public handle, including one that is not yours.'],
      ['Returns', 'Non-mutual accounts, with the coverage actually reached stated alongside.'],
      ['Stops at', 'Writing to the account: nothing is followed or unfollowed.'],
    ],
  },
  'tiktok-transcript': {
    steps: [
      'Copy the link to a public TikTok video and paste it in.',
      'Clear the human check and run it.',
      'Copy or download the spoken words.'],
    limits: [
      ['Accepts', 'One public TikTok video URL per run.'],
      ['Returns', 'The spoken words as plain text, ready to reuse.'],
      ['Stops at', 'Videos with no speech, which come back empty rather than invented.'],
    ],
  },
  'youtube-transcript': {
    steps: [
      'Paste a YouTube video link, in either the watch or short form.',
      'Clear the human check and run it.',
      'Read the transcript in full, then copy or download it.'],
    limits: [
      ['Accepts', 'One public YouTube video URL per run, at any length.'],
      ['Returns', 'The whole transcript on the page, not a preview.'],
      ['Stops at', 'Videos where no transcript exists, which say so.'],
    ],
  },
  'instagram-transcript': {
    steps: [
      'Paste the link to a public Reel or Instagram video post.',
      'Clear the human check and run it.',
      'Copy the transcript out, or download it as text.'],
    limits: [
      ['Accepts', 'A public Reel or video post URL.'],
      ['Returns', 'The spoken words from that single post.'],
      ['Stops at', 'Private accounts and image-only posts.'],
    ],
  },
};
